/* JS Modules */
import Vue from 'vue'
import App from './app.vue'
import { createRouter } from './router'
import { createStore } from './store'
import { createI18n } from './i18n'

import { sync } from 'vuex-router-sync'

import { languages, initials } from '~/config'
import { getCookie } from '@/util/methods'
import _isEmpty from 'lodash/isEmpty'

/* Vue Modules */
import VueLazyload from 'vue-lazyload'
import VueMoment from 'vue-moment'
import moment from 'moment'
import NoSSR from 'vue-no-ssr'

/* Vue Extensions */

/* Vue utilities */
import sticky from './util/sticky'
import lineClamp from './util/lineclamp'
import inview from './util/inview'
import mixins from './util/mixins'
import * as filters from './util/filters'

/* Vue UI Libraries */
import ElementUI from 'element-ui'
import 'element-ui/lib/theme-chalk/index.css'

/* Useful Global Component */
import Rating from 'views/components/rating'
import ElLink from 'views/components/link'
import UserPhoto from 'views/components/user-photo'
import RegistedImage from 'views/components/registed-image'
import UnitLoader from 'views/components/preloaders/unit'

import { http, cancelToken, setResponseHandler, setRequestHandler } from './util/http'
/**/
// vendor
import 'flatpickr/dist/flatpickr.css'
import 'swiper/dist/css/swiper.css'

import ko from '@/moment/locale/ko'
import en from '@/moment/locale/en'
moment.defineLocale('ko', ko)
moment.updateLocale('en', en)

Vue.mixin(mixins)

Vue.use(ElementUI, { ssr: true })
Vue.use(VueLazyload, {
  lazyComponent: true,
  preLoad: 1.5,
  attempt: 1,
  dispatchEvent: true,
  observer: true,
  observerOptions: { rootMargin: '100px', threshold: 0.3 },
  error: '/public/image-error.png',
})
Vue.use(VueMoment, { moment })

for (let key in filters) {
  Vue.filter(key, filters[key])
}

Vue.directive('sticky', sticky)
Vue.directive('lineClamp', lineClamp)
Vue.directive('inview', inview)

Vue.component('el-link', ElLink)
Vue.component('rating', Rating)
Vue.component('user-photo', UserPhoto)
Vue.component('registed-image', RegistedImage)
Vue.component('unit-loader', UnitLoader)
Vue.component('no-ssr', NoSSR)

export function createApp(context) {
  context = context || {}

  const router = createRouter()
  const store = createStore()
  const i18n = createI18n()

  sync(store, router)

  const url = context.url || location.pathname
  const langCode = context.langCode || url.split('/')[1]
  const currency = context.currency || getCookie('currency')
  const X_Requested_Lang = languages.includes(langCode) ? langCode : initials.fallbackLocale
  const X_Requested_Currency = currency || initials.currency

  moment.locale(X_Requested_Lang || initials.fallbackLocale)

  http.interceptors.request.handlers = []
  http.interceptors.response.handlers = []
  http.defaults.headers.common['X-Requested-Lang'] = X_Requested_Lang.toUpperCase()
  http.defaults.headers.common['X-Requested-Currency'] = X_Requested_Currency.toUpperCase()
  if (process.env.VUE_ENV == 'server') {
    http.defaults.headers.common['X-Forwarded-For'] = context.clientIp
  }

  Vue.prototype.$http = http
  Vue.prototype.$cancelToken = cancelToken
  store.$http = http

  store.state.onLive = context.isLive
  i18n.locale = X_Requested_Lang.toLowerCase()

  if (process.env.VUE_ENV == 'server') {
    const auth = context.cookie ? context.cookie.auth : null
    const authData = auth && JSON.parse(auth)
    if (authData && authData.tokenExpired < Date.now()) {
      context.cookie.auth = null
    }
  }

  const app = new Vue({
    router,
    store,
    i18n,
    ssrContext: context,
    render: (h) => h(App),
  })

  app.$moment.locale(langCode)

  const target = _isEmpty(context) ? app : context
  setRequestHandler(store.$http, target)
  setResponseHandler(store.$http, target)

  return { app, router, store }
}
